import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldAlert } from 'lucide-react';
import { permissionsForMatrix, type PermissionAction, type PermissionResource } from '@erp/shared';
import { usePermissions } from '@/features/auth/hooks/use-permissions';

export interface PermissionGuardProps {
  resource: PermissionResource;
  action: PermissionAction;
  children: ReactNode;
  /** Ohne Fallback wird ein Hinweis auf die fehlende Berechtigung angezeigt. */
  fallback?: ReactNode;
}

export function PermissionGuard({
  resource,
  action,
  children,
  fallback,
}: PermissionGuardProps): JSX.Element {
  const { t } = useTranslation();
  const { canAccess } = usePermissions();

  if (canAccess(resource, action)) {
    return <>{children}</>;
  }

  if (fallback !== undefined) {
    return <>{fallback}</>;
  }

  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-border p-10 text-center">
      <ShieldAlert className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
      <p className="text-sm font-medium">{t('permissions.deniedTitle')}</p>
      <p className="text-sm text-muted-foreground">
        {t('permissions.deniedDescription', {
          permission: permissionsForMatrix([{ resource, action }]).join(', '),
        })}
      </p>
    </div>
  );
}
